import { store } from '../store';
import { setLiveSocketReconnecting } from '../store/connectionSlice';
import {
  LIVE_RECONNECT_BASE_DELAY_MS,
  LIVE_RECONNECT_MAX_DELAY_MS,
  LiveSocketName,
} from '../constants/liveReconnect';

/**
 * Delay before reconnect attempt `attempt` (1-based): 1 s, 2 s, 4 s, 8 s, then
 * 10 s for every later attempt. There is no last attempt.
 */
export const getReconnectDelayMs = (attempt: number): number => {
  const exponent = Math.max(0, Math.floor(attempt) - 1);
  return Math.min(LIVE_RECONNECT_BASE_DELAY_MS * 2 ** exponent, LIVE_RECONNECT_MAX_DELAY_MS);
};

/**
 * One live socket's reconnect loop, shared by the recall, production, landmine,
 * competitor and calendar clients so they cannot drift apart again.
 *
 * The owning service calls `activate()` from `connect()` (the session wants the
 * socket), `markConnected()` from `onopen`, `scheduleReconnect()` from
 * `onclose` AND from a connect that failed before any socket existed (no
 * token), and `stop()` from `disconnect()`. While active there is no last
 * attempt; only `stop()` ends the loop. `reconnect` is the service's own
 * `connect()`, which fetches a fresh token and re-joins its room.
 *
 * The attempt's own rejection is only logged: the service has already
 * reported the failure through one of the calls above, and retrying from here
 * too would race the production socket's auth-failure path.
 */
export class LiveReconnectScheduler {
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private isLoopActive = false;

  constructor(
    private readonly socketName: LiveSocketName,
    private readonly reconnect: () => Promise<void>
  ) {}

  /** The session wants this socket. A direct connect supersedes a pending attempt. */
  activate(): void {
    this.isLoopActive = true;
    this.cancelPendingAttempt();
  }

  /** The socket opened: the next drop starts again at the first backoff step. */
  markConnected(): void {
    this.attempt = 0;
    this.cancelPendingAttempt();
    this.reportReconnecting(false);
  }

  /**
   * The socket dropped or an attempt failed. A no-op once stopped, and while an
   * attempt is already pending (a failed handshake reports both an error and a
   * close; it must cost one backoff step, not two).
   */
  scheduleReconnect(): void {
    if (!this.isLoopActive || this.timer) return;

    this.attempt += 1;
    const attempt = this.attempt;
    const delayMs = getReconnectDelayMs(attempt);
    this.reportReconnecting(true);
    console.info(`[LiveReconnect] ${this.socketName}: attempt ${attempt} in ${delayMs} ms`);

    this.timer = setTimeout(() => {
      this.timer = null;
      this.reconnect().catch((error: unknown) => {
        console.warn(`[LiveReconnect] ${this.socketName}: attempt ${attempt} failed`, error);
      });
    }, delayMs);
  }

  /** Restart the backoff at its first step (the production socket's token-refresh retry). */
  resetBackoff(): void {
    this.attempt = 0;
  }

  /** The owner closed the socket on purpose (session over, sign-out): no more attempts. */
  stop(): void {
    this.isLoopActive = false;
    this.attempt = 0;
    this.cancelPendingAttempt();
    this.reportReconnecting(false);
  }

  /**
   * False once `stop()` ran. A connect that was awaiting its token when the
   * session ended checks this and opens nothing.
   */
  isActive(): boolean {
    return this.isLoopActive;
  }

  private cancelPendingAttempt(): void {
    if (!this.timer) return;
    clearTimeout(this.timer);
    this.timer = null;
  }

  private reportReconnecting(isReconnecting: boolean): void {
    const isReported = store.getState().connection.reconnectingSockets.includes(this.socketName);
    if (isReported === isReconnecting) return;
    store.dispatch(setLiveSocketReconnecting({ socket: this.socketName, isReconnecting }));
  }
}
